import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { invoiceAPI, translatorAPI, submissionAPI } from '@/utils/api';
import { formatCurrency, formatDate, formatMonth, getCurrentMonth } from '@/utils/formatters';
import { Receipt, Download, Plus, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminInvoices = () => {
  const { user } = useOutletContext();
  const [invoices, setInvoices] = useState([]);
  const [translators, setTranslators] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [form, setForm] = useState({
    translator_id: '',
    month: getCurrentMonth()
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [invoicesRes, translatorsRes, submissionsRes] = await Promise.all([
        invoiceAPI.list({}),
        translatorAPI.listTranslators(),
        submissionAPI.list()
      ]);
      setInvoices(invoicesRes.data);
      setTranslators(translatorsRes.data);
      setSubmissions(submissionsRes.data.filter(s => s.status === 'approved'));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      await invoiceAPI.generate(form);
      toast.success('Invoice generated successfully!');
      setShowDialog(false);
      setForm({ translator_id: '', month: getCurrentMonth() });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleDownloadPDF = (invoiceId, invoiceNumber) => {
    window.open(invoiceAPI.downloadPDF(invoiceId), '_blank');
  };
  
  const getStatusColor = (status) => {
    const colors = {
      generated: 'bg-creative/10 text-creative',
      sent: 'bg-warning/10 text-warning',
      paid: 'bg-success/10 text-success',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };
  
  const getAvailableTranslators = () => {
    return submissions
      .filter(s => !invoices.find(inv => inv.translator_id === s.translator_id && inv.month === s.month))
      .map(s => {
        const translator = translators.find(t => t.user_id === s.translator_id);
        return translator ? { ...translator, submission_month: s.month } : null;
      })
      .filter((t, index, self) => t && self.findIndex(x => x?.user_id === t.user_id) === index);
  };
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-primary mb-2">Manage Invoices</h1>
            <p className="text-muted-foreground">Generate and manage translator invoices with TDS calculation</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button size="lg" data-testid="btn-generate-invoice">
                <Plus className="w-5 h-5 mr-2" />
                Generate Invoice
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Generate Invoice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleGenerateInvoice} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="translator">Translator (Approved Work) *</Label>
                  <Select value={form.translator_id} onValueChange={(value) => setForm({...form, translator_id: value})} required>
                    <SelectTrigger data-testid="select-translator">
                      <SelectValue placeholder="Select translator" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableTranslators().map((translator) => (
                        <SelectItem key={translator.user_id} value={translator.user_id}>
                          {translator.full_name} - {formatMonth(translator.submission_month)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getAvailableTranslators().length === 0 && (
                    <p className="text-sm text-warning">No approved work submissions available for invoicing</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="month">Month (YYYY-MM) *</Label>
                  <Input
                    id="month"
                    type="month"
                    value={form.month}
                    onChange={(e) => setForm({...form, month: e.target.value})}
                    required
                    data-testid="input-month"
                  />
                </div>
                
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-primary flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    TDS Calculation Rules
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                    <li>If invoice total &gt; ₹30,000: TDS = 1% of total</li>
                    <li>If invoice total ≤ ₹30,000: No TDS</li>
                    <li>Net Payable = Subtotal - TDS</li>
                  </ul>
                </div>
                
                <Button type="submit" className="w-full" size="lg" disabled={generating} data-testid="btn-save-invoice">
                  {generating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Generating Invoice...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Invoice
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-accent" />
              All Invoices ({invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No invoices yet. Generate invoices for approved work.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card 
                    key={invoice.invoice_id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`invoice-${invoice.invoice_id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <FileText className="w-6 h-6 text-accent" />
                            <div>
                              <h3 className="text-xl font-heading font-bold text-primary">{invoice.invoice_number}</h3>
                              <p className="text-sm text-muted-foreground">Period: {formatMonth(invoice.month)}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Translator</p>
                              <p className="text-sm font-medium text-primary">{invoice.translator_name}</p>
                            </div>
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Subtotal</p>
                              <p className="text-lg font-semibold text-primary">{formatCurrency(invoice.subtotal)}</p>
                            </div>
                            
                            {invoice.tds_amount > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">TDS (1%)</p>
                                <p className="text-lg font-semibold text-warning">-{formatCurrency(invoice.tds_amount)}</p>
                              </div>
                            )}
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Net Payable</p>
                              <p className="text-lg font-bold text-success">{formatCurrency(invoice.net_payable)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <Badge className={getStatusColor(invoice.status)}>
                              {invoice.status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Date: {formatDate(invoice.invoice_date)}
                            </span>
                            {invoice.payment_date && (
                              <span className="text-sm text-success">
                                Paid: {formatDate(invoice.payment_date)}
                              </span>
                            )}
                          </div>
                          
                          {invoice.payment_reference && (
                            <p className="text-sm text-muted-foreground mt-2">Ref: {invoice.payment_reference}</p>
                          )}
                        </div>
                        
                        <Button
                          onClick={() => handleDownloadPDF(invoice.invoice_id, invoice.invoice_number)}
                          variant="outline"
                          size="sm"
                          data-testid={`btn-download-${invoice.invoice_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminInvoices;
